// Tier catalogue: the single source of truth for tier rank, pricing anchors,
// founder prices and AI-credit grants. Pure data + lookups, fully unit-tested.

import type { Tier, EntitlementId, CreditGrant } from './types';

export const ALL_TIERS: readonly Tier[] = ['free', 'starter', 'creator', 'business'] as const;

/** Paid tiers map 1:1 to RevenueCat entitlement ids. */
export const PAID_ENTITLEMENTS: readonly EntitlementId[] = [
  'starter',
  'creator',
  'business',
] as const;

export interface TierSpec {
  id: Tier;
  /** Ordering: higher = stronger. Used for entitlement resolution and gating. */
  rank: number;
  /** Regular (anchor) prices in EUR. */
  regularMonthly: number;
  regularAnnual: number;
  /** Founder-deal prices in EUR (null for free). */
  founderMonthly: number | null;
  founderAnnual: number | null;
  /** AI-credit allotment and how it replenishes. */
  creditGrant: CreditGrant;
}

export const TIERS: Record<Tier, TierSpec> = {
  free: {
    id: 'free',
    rank: 0,
    regularMonthly: 0,
    regularAnnual: 0,
    founderMonthly: null,
    founderAnnual: null,
    creditGrant: { amount: 3, period: 'once' }, // lifetime trial teaser (AC-C1)
  },
  starter: {
    id: 'starter',
    rank: 1,
    regularMonthly: 4.99,
    regularAnnual: 39,
    founderMonthly: 2.99,
    founderAnnual: 22.99,
    creditGrant: { amount: 5, period: 'daily' }, // AC-C5
  },
  creator: {
    id: 'creator',
    rank: 2,
    regularMonthly: 14.99,
    regularAnnual: 119,
    founderMonthly: 9.99,
    founderAnnual: 79.99,
    creditGrant: { amount: 500, period: 'monthly' }, // AC-C6
  },
  business: {
    id: 'business',
    rank: 3,
    regularMonthly: 49,
    regularAnnual: 399,
    founderMonthly: 29,
    founderAnnual: 239,
    creditGrant: { amount: 3000, period: 'monthly' }, // AC-C6
  },
};

export function tierRank(tier: Tier): number {
  return TIERS[tier].rank;
}

export function creditGrantFor(tier: Tier): CreditGrant {
  return TIERS[tier].creditGrant;
}

function isEntitlementId(id: string): id is EntitlementId {
  return (PAID_ENTITLEMENTS as readonly string[]).includes(id);
}

/**
 * Resolve the effective tier from the set of active RevenueCat entitlement ids.
 * The strongest active entitlement wins; unknown ids are ignored; none = free.
 * On-device entitlements are advisory UX only — the server remains authoritative.
 */
export function tierFromEntitlements(activeEntitlementIds: readonly string[]): Tier {
  let best: Tier = 'free';
  for (const id of activeEntitlementIds) {
    if (isEntitlementId(id) && tierRank(id) > tierRank(best)) {
      best = id;
    }
  }
  return best;
}
